import { IconChevronRight } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { Breadcrumb } from './Breadcrumb'
import { BreadcrumbLink } from './BreadcrumbLink'
import messages from './messages'
import { BreadcrumbsItemStyled } from './styles/BreadcrumbsItemStyled'
import { BreadcrumbsListStyled } from './styles/BreadcrumbsListStyled'
import { BreadcrumbsStyled } from './styles/BreadcrumbsStyled'
import { type Crumb, type CurrentPageCrumb } from './types'

export interface IBreadcrumbsProps {
  /**
   * A breadcrumb trail: zero or more linked crumbs followed by the current page,
   * which carries no link.
   *
   * Building the trail in a variable widens it to an array and loses the
   * positional guarantee — pin it with `satisfies IBreadcrumbsProps['crumbs']`.
   *
   * Spread a conditional crumb before the final crumb, never after it.
   */
  crumbs: readonly [] | readonly [...(readonly Crumb[]), CurrentPageCrumb]
}

export const Breadcrumbs = ({ crumbs }: IBreadcrumbsProps) => {
  const intl = useProcurifyIntl()

  if (crumbs.length === 0) return null

  return (
    <BreadcrumbsStyled aria-label={intl.formatMessage(messages.navAriaLabel)}>
      <BreadcrumbsListStyled>
        {crumbs.map((crumb, index) => (
          <BreadcrumbsItemStyled key={`${index}-${crumb.name}`}>
            {index > 0 ? <IconChevronRight /> : null}
            {crumb.link ? (
              <BreadcrumbLink
                to={crumb.link}
                onClick={crumb.onClick}
                escapeCaseFormatting={crumb.escapeCaseFormatting}
              >
                {crumb.name}
              </BreadcrumbLink>
            ) : (
              <Breadcrumb escapeCaseFormatting={crumb.escapeCaseFormatting}>
                {crumb.name}
              </Breadcrumb>
            )}
          </BreadcrumbsItemStyled>
        ))}
      </BreadcrumbsListStyled>
    </BreadcrumbsStyled>
  )
}

Breadcrumbs.displayName = 'Breadcrumbs'
